import { css } from 'styled-components';
import brand from './brand';
import { fluidAdjusted } from './fluid';
import { Device, from } from './media';
export const gradientStyle = css `
  display: inline-block;
  background: linear-gradient(90deg, var(--leftGradient), var(--rightGradient));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;
export const headingStyles = css `
  --leftGradient: ${brand.secondary.red5};
  --rightGradient: ${brand.secondary.red1};
  color: currentColor;
  margin: 0 0 ${fluidAdjusted(16, 18)};
  font-family: var(--fontFamily);
  font-weight: 900;
  padding-bottom: 7px;

  @media ${from(Device.TabletLarge)} {
    letter-spacing: -0.024em;
  }

  &::selection {
    -webkit-text-fill-color: currentColor;
  }
`;
export const h1 = css `
  ${headingStyles};

  font-size: ${fluidAdjusted(32, 64)};
  line-height: ${fluidAdjusted(40, 74)};
`;
export const h2 = css `
  ${headingStyles};
  font-size: ${fluidAdjusted(28, 48)};
  line-height: ${fluidAdjusted(36, 58)};
`;
export const h3 = css `
  ${headingStyles};
  font-size: ${fluidAdjusted(24, 40)};
  line-height: ${fluidAdjusted(30, 50)};
`;
export const h4 = css `
  ${headingStyles};
  font-size: ${fluidAdjusted(20, 36)};
  line-height: ${fluidAdjusted(26, 42)};
`;
export const h5 = css `
  ${headingStyles};
  font-size: ${fluidAdjusted(18, 32)};
  line-height: ${fluidAdjusted(24, 40)};
`;
export const h6 = css `
  ${headingStyles};
  font-size: ${fluidAdjusted(16, 24)};
  line-height: ${fluidAdjusted(20, 30)};
`;
export const paragraphStyles = css `
  font-family: var(--fontFamily);
  color: currentColor;
  margin: 0 0 ${fluidAdjusted(14, 16)};
  font-weight: 300;
`;
export const paragraphLarge = css `
  ${paragraphStyles};
  font-size: ${fluidAdjusted(18, 28)};
  line-height: ${fluidAdjusted(28, 44)};
`;
export const paragraphMedium = css `
  ${paragraphStyles};
  font-size: ${fluidAdjusted(16, 22)};
  line-height: ${fluidAdjusted(26, 36)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};
  font-size: ${fluidAdjusted(15, 18)};
  line-height: ${fluidAdjusted(24, 28)};
`;
export const paragraphExSmall = css `
  ${paragraphStyles};
  font-size: ${fluidAdjusted(13, 13)};
  line-height: ${fluidAdjusted(20, 20)};
`;
export const paragraphTiny = css `
  ${paragraphStyles};
  font-size: ${fluidAdjusted(10, 10)};
  line-height: ${fluidAdjusted(16, 16)};
`;
const CtaStyles = css `
  color: currentColor;
  margin: 0 0 ${fluidAdjusted(14, 16)};
`;
export const CtaLarge = css `
  ${CtaStyles};
  font-size: ${fluidAdjusted(24, 28)};
  line-height: ${fluidAdjusted(28, 32)};
`;
export const CtaMedium = css `
  ${CtaStyles};
  font-size: ${fluidAdjusted(20, 20)};
  line-height: ${fluidAdjusted(24, 24)};
`;
export const CtaSmall = css `
  ${CtaStyles};
  font-size: ${fluidAdjusted(18, 18)};
  line-height: ${fluidAdjusted(22, 22)};
`;
export const link = css `
  display: inline-block;
  color: ${brand.secondary.red5};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    color: ${brand.secondary.red1};
    text-decoration: none;
  }
`;
export const table = css `
  background: ${brand.white};
  border-collapse: collapse;
  color: ${brand.black};
  margin-bottom: 1em;
  width: 100%;
  overflow: auto;
  display: block;

  thead {
    tr {
      background: ${brand.primary.red};

      td,
      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background: ${brand.white};
      }

      &:nth-child(even) {
        background: ${brand.grey.grey97};
      }

      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
      }

      td {
        border: 2px solid ${brand.white};
        font-size: 16px;
        letter-spacing: -0.01em;
        line-height: 20px;
        padding: 12px 16px;
        text-align: left;
      }
    }
  }
`;
export const blockquote = css `
  position: relative;
  ${paragraphMedium}
  text-align: left;
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h1}
  }

  h2,
  .h2-styles {
    ${h2}
  }

  h3,
  .h3-styles {
    ${h3}
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${h5}
  }

  h6,
  .h6-styles {
    ${h6}
  }

  p {
    ${paragraphMedium}
    margin: 0 0 1em;
  }

  p.large {
    ${paragraphLarge}
    margin: 0 0 1em;
  }

  p.small {
    ${paragraphSmall}
    margin: 0 0 1em;
  }

  p.ex-small {
    ${paragraphExSmall}
  }

  p.tiny {
    ${paragraphTiny}
  }

  p.text-align-center {
    text-align: center;
  }

  p.text-align-right {
    text-align: right;
  }

  p.text-align-justify {
    text-align: justify;
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphMedium};
    margin: 0 0 0.5em;

    li {
      margin: 0 0 0.33em;
    }
  }

  ol {
    list-style: decimal;
    padding-left: 1.2em;
  }

  ul {
    list-style: disc;
    padding-left: 1.2em;
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 4px;
    margin: ${fluidAdjusted(40, 80)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  blockquote {
    ${blockquote};
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
